import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon, MailIcon, PlusIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { Toggle } from '../../components/ui/Toggle';
import { EmailPreview } from '../../components/EmailPreview';
import { formatInterval } from '../../utils/format';
export function Notifications() {
  const {
    plan,
    recipients,
    defaults,
    updateDefaults,
    addRecipient,
    removeRecipient,
    verifyRecipient
  } = useAccount();
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [testState, setTestState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const limitReached = plan.recipientLimit !== 'unlimited' && recipients.length >= plan.recipientLimit;
  function submitRecipient(event: React.FormEvent) {
    event.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (recipients.some(r => r.email === newEmail.trim())) {
      setEmailError('That address is already on the list.');
      return;
    }
    setEmailError(null);
    addRecipient(newEmail.trim());
    setNewEmail('');
  }
  function sendTest() {
    setTestState('sending');
    window.setTimeout(() => {
      setTestState('sent');
      window.setTimeout(() => setTestState('idle'), 4000);
    }, 1100);
  }
  return <div className="space-y-5" data-id="element-1158" data-mp="4vm03q419uu3">
      <div data-id="element-1159" data-mp="9scdg5ogecbt">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-1160" data-mp="9s2psoqvsh9a">
          Notifications
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-ink-muted" data-id="element-1161" data-mp="1r4twqm6eq3s">
          These are the account defaults. Every new domain inherits them, and
          you can override any of it per monitor.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr] lg:items-start" data-id="element-1162" data-mp="bqvm2ln9kt0p">
        <div className="space-y-5" data-id="element-1163" data-mp="6qq0nn7l82r2">
          <Panel as="section" data-id="element-1164" data-mp="6pod4j9dxnsg">
            <PanelHeader title="Recipients" description={plan.recipientLimit === 'unlimited' ? 'Unlimited addresses on Pro. Everyone must confirm before we send to them.' : `${plan.name} allows ${plan.recipientLimit} ${plan.recipientLimit === 1 ? 'address' : 'addresses'}. Everyone must confirm before we send to them.`} data-id="element-1165" data-mp="kqr2b4vofe7l" />
            <ul className="divide-y divide-line" data-id="element-1166" data-mp="14cnfnamrdpy9">
              {recipients.map(recipient => <li key={recipient.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-1167" data-mp="zdk9fmv1ldop">
                  <MailIcon className="h-4 w-4 shrink-0 text-ink-soft" aria-hidden="true" data-id="element-1168" data-mp="1n3bqzhrcidro" />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-1169" data-mp="195dd0dgxsjcp">
                    {recipient.email}
                  </span>
                  {recipient.verified ? <span className="flex items-center gap-1.5 text-[11px] font-medium text-healthy-ink" data-id="element-1170" data-mp="zc6bj3pbhnhw">
                      <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1171" data-mp="146akfsi585lm" />
                      Confirmed
                    </span> : <>
                      <span className="rounded-full border border-warn-border bg-warn-soft px-2 py-0.5 text-[11px] font-medium text-warn-ink" data-id="element-1172" data-mp="gfn027r1jni6">
                        Awaiting confirmation
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => verifyRecipient(recipient.id)} data-id="element-1173" data-mp="1oo2n0529aipm">
                        Resend
                      </Button>
                    </>}
                  <button type="button" onClick={() => removeRecipient(recipient.id)} aria-label={`Remove ${recipient.email}`} className="inline-flex h-7 w-7 items-center justify-center rounded-control text-ink-soft transition-colors duration-150 ease-snap hover:bg-critical-soft hover:text-critical-ink" data-id="element-1174" data-mp="19z6d02gn2vmd">
                    <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1175" data-mp="r2oen27abxh5" />
                  </button>
                </li>)}
            </ul>

            <form onSubmit={submitRecipient} className="border-t border-line px-5 py-4" noValidate data-id="element-1176" data-mp="2lelad7crfpi0">
              <div className="flex flex-wrap gap-2" data-id="element-1177" data-mp="2swjhx6bdsqil">
                <label htmlFor="new-recipient" className="sr-only" data-id="element-1178" data-mp="2xwfbebmgczjw">
                  Add a recipient
                </label>
                <TextInput id="new-recipient" type="email" placeholder="name@company.com" className="min-w-[200px] flex-1" value={newEmail} invalid={Boolean(emailError)} disabled={limitReached} onChange={event => setNewEmail(event.target.value)} data-id="element-1179" data-mp="32oyz41eutb2f" />
                <Button type="submit" variant="secondary" disabled={limitReached} data-id="element-1180" data-mp="2jzscw4euq1bv">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1181" data-mp="2oydbjzeggcy7" />
                  Add recipient
                </Button>
              </div>
              {emailError && <p className="mt-2 text-xs text-critical-ink" data-id="element-1182" data-mp="45idgnrt0jhp">{emailError}</p>}
              {limitReached && <p className="mt-2 text-xs text-warn-ink" data-id="element-1183" data-mp="207v6kktxmnxr">
                  {plan.name} allows {String(plan.recipientLimit)} recipients.{' '}
                  <Link to="/app/billing" className="font-medium underline decoration-1 underline-offset-2" data-id="element-1184" data-mp="29awb4h35g6zb">
                    Pro removes the limit
                  </Link>
                  .
                </p>}
            </form>
          </Panel>

          <Panel as="section" data-id="element-1185" data-mp="1cl6i1gaf3x86">
            <PanelHeader title="Defaults for new monitors" description="Changing these does not touch monitors you have already set up." data-id="element-1186" data-mp="1clyc2mod9mwc" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2" data-id="element-1187" data-mp="1lk0dv4fxcjko">
              <Field label="Warning window" htmlFor="default-window" data-id="element-1188" data-mp="xupftupyl3a8">
                <SelectInput id="default-window" value={String(defaults.warningWindowDays)} disabled={Boolean(plan.warningWindowFixed)} onChange={event => updateDefaults({
                warningWindowDays: Number(event.target.value)
              })} options={[7, 14, 21, 30, 45, 60].map(days => ({
                value: String(days),
                label: `${days} days`
              }))} data-id="element-1189" data-mp="xsuzktpmtzch" />
              </Field>

              <Field label="Ping interval" htmlFor="default-interval" data-id="element-1190" data-mp="12ecbeq9ajq2m">
                <SelectInput id="default-interval" value={String(defaults.pingIntervalMinutes)} onChange={event => updateDefaults({
                pingIntervalMinutes: Number(event.target.value)
              })} options={[30, 15, 5, 1].map(minutes => ({
                value: String(minutes),
                label: plan.pingOptions.includes(minutes) ? formatInterval(minutes) : `${formatInterval(minutes)} — Pro only`,
                disabled: !plan.pingOptions.includes(minutes)
              }))} data-id="element-1191" data-mp="17erorhtkoxkl" />
              </Field>

              <Field label="Failures before alerting" htmlFor="default-threshold" className="sm:col-span-2" data-id="element-1192" data-mp="2lh0pebo7k068">
                <SelectInput id="default-threshold" value={String(defaults.failureThreshold)} onChange={event => updateDefaults({
                failureThreshold: Number(event.target.value)
              })} options={[1, 2, 3, 5].map(count => ({
                value: String(count),
                label: `${count} consecutive ${count === 1 ? 'check' : 'checks'}`
              }))} data-id="element-1193" data-mp="3i6i2hbpisohy" />
              </Field>

              <div className="sm:col-span-2" data-id="element-1194" data-mp="1iqhrog5xehoq">
                <p className="text-[13px] font-medium text-ink" data-id="element-1195" data-mp="1nmq0lbtj5eng">
                  Reminder thresholds
                </p>
                <p className="mt-0.5 text-xs text-ink-soft" data-id="element-1196" data-mp="1robgl61d291v">
                  Days before expiry that trigger an email.
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1197" data-mp="21naim5rmf6fw">
                  {[30, 21, 14, 7, 3, 1].map(days => {
                  const active = defaults.reminderCadence.includes(days);
                  return <button key={days} type="button" aria-pressed={active} onClick={() => updateDefaults({
                    reminderCadence: active ? defaults.reminderCadence.filter(d => d !== days) : [...defaults.reminderCadence, days].sort((a, b) => b - a)
                  })} className={`tabular rounded-control border px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 ease-snap ${active ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1198" data-mp="2bcke7vlob78r">
                        {days}d
                      </button>;
                })}
                </div>
              </div>
            </div>
          </Panel>

          <Panel as="section" data-id="element-1199" data-mp="e35ih6qf2tbp">
            <PanelHeader title="When we email you" description="Downtime alerts always go out immediately, whatever you choose here." data-id="element-1200" data-mp="e3xa66gw86wp" />
            <div className="space-y-4 px-5 py-5" data-id="element-1201" data-mp="akya9zve1gr9">
              <fieldset data-id="element-1202" data-mp="5seuqxd54bjt">
                <legend className="sr-only" data-id="element-1203" data-mp="5s555aw0c6en">Alert cadence</legend>
                <div className="grid gap-2 sm:grid-cols-2" data-id="element-1204" data-mp="tc0m7yp7pqm">
                  {([{
                  value: 'immediate',
                  title: 'Every event, immediately',
                  body: 'One email per threshold crossed.'
                }, {
                  value: 'daily-digest',
                  title: 'One daily digest',
                  body: 'Everything from the last 24 hours in one email.'
                }] as const).map(option => <label key={option.value} className={`cursor-pointer rounded-card border px-4 py-3 transition-colors duration-150 ease-snap ${defaults.cadence === option.value ? 'border-brand bg-brand-soft' : 'border-line hover:border-line-strong'}`} data-id="element-1205" data-mp="3593d46ztnvp8">
                      <span className="flex items-center gap-2.5" data-id="element-1206" data-mp="2sowlk16b32yo">
                        <input type="radio" name="cadence" className="h-3.5 w-3.5 border-line-strong text-brand focus:ring-brand" checked={defaults.cadence === option.value} onChange={() => updateDefaults({
                      cadence: option.value
                    })} data-id="element-1207" data-mp="2wq9tqhwvfi8r" />
                        <span className="text-[13px] font-medium text-ink" data-id="element-1208" data-mp="2dyh1ippmm123">
                          {option.title}
                        </span>
                      </span>
                      <span className="mt-1.5 block pl-6 text-xs leading-relaxed text-ink-muted" data-id="element-1209" data-mp="24yk9qf7xwm2t">
                        {option.body}
                      </span>
                    </label>)}
                </div>
              </fieldset>

              <div className="rounded-card border border-line bg-canvas px-4 py-4" data-id="element-1210" data-mp="1uei8aw0ycq22">
                <div className="flex items-start justify-between gap-4" data-id="element-1211" data-mp="1qd6c27nkp6kw">
                  <div data-id="element-1212" data-mp="1qbv9uc9s06vw">
                    <p className="text-[13px] font-medium text-ink" data-id="element-1213" data-mp="24cxymws5nvai">
                      Quiet hours
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted" data-id="element-1214" data-mp="1zemo0ju1dgr8">
                      Hold expiry reminders overnight and send them in the
                      morning.
                    </p>
                  </div>
                  <Toggle checked={defaults.quietHoursEnabled} onChange={() => updateDefaults({
                  quietHoursEnabled: !defaults.quietHoursEnabled
                })} label="Enable quiet hours" data-id="element-1215" data-mp="294z5u4aguvto" />
                </div>
                {defaults.quietHoursEnabled && <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-1216" data-mp="lcoum19tx5o9">
                    <Field label="From" htmlFor="quiet-from" data-id="element-1217" data-mp="q4q63kz6asd8">
                      <TextInput id="quiet-from" type="time" className="tabular" value={defaults.quietFrom} onChange={event => updateDefaults({
                    quietFrom: event.target.value
                  })} data-id="element-1218" data-mp="u6k3nke2ng96" />
                    </Field>
                    <Field label="Until" htmlFor="quiet-to" data-id="element-1219" data-mp="bg2flt46p8u5">
                      <TextInput id="quiet-to" type="time" className="tabular" value={defaults.quietTo} onChange={event => updateDefaults({
                    quietTo: event.target.value
                  })} data-id="element-1220" data-mp="bhn7tc310fou" />
                    </Field>
                  </div>}
              </div>
            </div>
          </Panel>

          <Panel as="section" data-id="element-1221" data-mp="1n81mjt1jonea">
            <PanelHeader title="Slack and webhooks" description={plan.integrations ? 'Post alerts into a channel or your own endpoint alongside email.' : 'Available on Pro. Email alerts keep working on every plan.'} action={plan.integrations ? <Button variant="secondary" size="sm" onClick={sendTest} disabled={testState === 'sending'} data-id="element-1223" data-mp="14as52hfs5jzo">
                    {testState === 'sending' ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-1224" data-mp="2umoaa3kvtad4" /> : <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1225" data-mp="wrivoo79xlam" />}
                    {testState === 'sending' ? 'Sending' : testState === 'sent' ? 'Test sent' : 'Send a test'}
                  </Button> : <Button variant="secondary" size="sm" to="/app/billing" data-id="element-1226" data-mp="1arjuly3zaing">
                    Move to Pro
                  </Button>} data-id="element-1222" data-mp="1n701qwuq8mjd" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2" data-id="element-1227" data-mp="11palncocyxc6">
              <Field label="Slack webhook URL" htmlFor="slack" data-id="element-1228" data-mp="16ox2rx7b4xq9">
                <TextInput id="slack" placeholder="https://hooks.slack.com/services/…" value={defaults.slackWebhook} disabled={!plan.integrations} onChange={event => updateDefaults({
                slackWebhook: event.target.value
              })} data-id="element-1229" data-mp="16r9p0mk3lu94" />
              </Field>
              <Field label="Custom webhook URL" htmlFor="webhook" data-id="element-1230" data-mp="3ewip1dxmku99">
                <TextInput id="webhook" placeholder="https://acme.com/hooks/watchtower" value={defaults.webhookUrl} disabled={!plan.integrations} onChange={event => updateDefaults({
                webhookUrl: event.target.value
              })} data-id="element-1231" data-mp="3svicii9ej33u" />
              </Field>
            </div>
          </Panel>
        </div>

        <div className="space-y-3 lg:sticky lg:top-8" data-id="element-1232" data-mp="39zs7em669qlq">
          <h2 className="text-[13px] font-semibold text-ink" data-id="element-1233" data-mp="356pemg97yl6l">
            What your recipients will get
          </h2>
          <EmailPreview kind={defaults.cadence === 'daily-digest' ? 'digest' : 'expiry'} domain="shop.acme.com" days={9} recipients={recipients.filter(r => r.verified).map(r => r.email)} reminderCadence={defaults.cadence === 'immediate' ? defaults.reminderCadence : undefined} quietHours={defaults.quietHoursEnabled ? `${defaults.quietFrom}–${defaults.quietTo}` : null} data-id="element-1234" data-mp="2hg3cf5lwbv4i" />
          <p className="text-xs leading-relaxed text-ink-soft" data-id="element-1235" data-mp="29xba4wmdvdor">
            This preview follows the settings on this page, so you can see the
            result before anything is sent for real.
          </p>
        </div>
      </div>
    </div>;
}